/**
 * Object mounting constants, and the vertical/mount rules read off them.
 *
 * The TABLE lives in `components/3d/utils/objectTypeConfigs.js` — one source
 * for "how does this object attach to the world?". This module used to carry a
 * second copy (`OBJECT_MOUNT_CONFIG`) that was authoritative for `mountType` /
 * `mountHeight` / `requiresWall` while the other owned `wallOffset` /
 * `facesWall` / `wallAlignment` / `verticalAlignment`. Two tables, each right
 * about different fields, drifting on four values — see
 * `claude-docs/2026-08-27-mounting-tables-map.md`.
 *
 * The named constants below stay here: the merged table is written in terms of
 * them, which reads better than bare numbers.
 */
import {
  OBJECT_CONFIGS,
  canonicalObjectType,
} from '../components/3d/utils/objectTypeConfigs';

export const MOUNT_TYPES = {
  FLOOR: 'floor',
  WALL: 'wall',
  CEILING: 'ceiling',
  // Held by a COUNTERTOP — a cooktop cut into it, an undermount kitchen sink
  // dropped through it. A fourth way to hold something up, and the one this
  // module cannot compute: the counter's height is known where the counter is
  // (kitchen post-processing), not here.
  COUNTER: 'counter',
};

/**
 * Vertical mounting heights (in meters, measured from floor)
 * For wall-mounted objects that mount at a specific height
 */
export const MOUNT_HEIGHTS = {
  // Bathroom fixtures
  MIRROR: 1.6, // 1.6m above floor (eye level, ~5'3")
  WALL_LIGHT: 1.68, // 1.68m above floor (5'6")
  OVERHEAD_WALL_LIGHT: 2.1, // 2.1m above floor (7 feet)
  TOWEL_BAR: 1.2, // 1.2m above floor (4 feet)
  // Wall-hung toilet rim — ADA spec is 17–19" from floor; 0.43m = 17".
  // Convention: this is the world-Y of the bowl rim. Center Y is
  // computed in getVerticalPosition as `floorY + RIM - height/2`, which
  // puts the TOP of the model's bbox at the rim line. Models authored
  // with a stylized lid above the rim will have that lid extending above
  // this line; that's fine and matches real wall-hung product geometry.
  TOILET_WALL_HUNG_RIM: 0.43,

  // Kitchen fixtures
  WALL_CABINET: 1.5, // 1.5m above floor (bottom of cabinet)
  RANGE_HOOD: 1.8, // 1.8m above floor (above stove)

  // General
  WALL_ART: 1.5, // 1.5m above floor (eye level for art)
  SHELF: 1.8, // 1.8m above floor
  TV_MOUNT: 1.2, // 1.2m above floor (center of TV)

  // Ceiling (measured from floor, not ceiling)
  CEILING_LIGHT: 2.44, // 2.44m above floor (8 feet ceiling)
  CEILING_FAN: 2.44, // 2.44m above floor
  RECESSED_LIGHT: 2.44, // 2.44m above floor
  // Hanging planter — suspended ~1 foot below an 8-foot ceiling.
  HANGING_PLANTER: 2.13, // 2.13m above floor (7 feet)
};

/**
 * Wall offset distances (in meters)
 * How far objects should be from the wall surface
 */
export const WALL_OFFSETS = {
  FLUSH: 0, // Completely flush with wall
  SMALL: 0.02, // 2cm - for mounting hardware clearance (mirrors)
  MEDIUM: 0.05, // 5cm - standard offset
  LARGE: 0.075, // 7.5cm - 3 inches (toilet clearance)
  FURNITURE: 0.1, // 10cm - furniture against wall
};

/**
 * Get mounting configuration for an object type.
 *
 * For toilets, the optional `doc` argument lets callers branch on the
 * per-doc `mountType` field (a data field on the fixture; the one-off
 * admin review UI that backfilled it has been retired).
 * When `doc.mountType === 'wall-hung'`, the returned config flips to
 * WALL mount with the ADA-spec rim height and a flush wall offset.
 *
 * @param {string} objectType - The type of object
 * @param {Object} [doc] - Optional fixture doc/instance (looks for `mountType`)
 * @returns {Object} Mount configuration
 */
export function getMountConfig(objectType, doc = null) {
  // ONE table. `OBJECT_CONFIGS` carries the mount fields this module used to
  // duplicate, and `canonicalObjectType` folds the synonyms (`stove` ->
  // `range`, `range-hood` -> `vent-hood`) so both readers resolve a type the
  // same way — the two tables previously disagreed even about the NAME.
  const base =
    OBJECT_CONFIGS[canonicalObjectType(objectType)] || OBJECT_CONFIGS.default;
  if (objectType === 'toilet' && doc?.mountType === 'wall-hung') {
    return {
      ...base,
      mountType: MOUNT_TYPES.WALL,
      mountHeight: MOUNT_HEIGHTS.TOILET_WALL_HUNG_RIM,
      wallOffset: WALL_OFFSETS.FLUSH,
    };
  }
  return base;
}

/**
 * Get the vertical position for an object.
 *
 * For wall-hung toilets (objectType === 'toilet' && doc.mountType ===
 * 'wall-hung'), the returned Y places the TOP of the bbox at the rim
 * line (`floorY + RIM`). This is the simplest model-agnostic convention
 * — models with a stylized lid will have it sitting slightly above the
 * rim; models with hidden mounting flange will have it dipping below
 * floor (occluded by floor geometry). For all other mount types the
 * behavior is unchanged.
 *
 * @param {string} objectType - The type of object
 * @param {number} floorY - Floor Y position
 * @param {number} objectHeight - Height of the object
 * @param {number} ceilingY - Ceiling Y position (optional)
 * @param {Object} [doc] - Optional fixture doc/instance (looks for `mountType`)
 * @returns {number} Y position for object center
 */
export function getVerticalPosition(
  objectType,
  floorY,
  objectHeight,
  ceilingY = null,
  doc = null
) {
  const config = getMountConfig(objectType, doc);

  switch (config.mountType) {
    case MOUNT_TYPES.FLOOR:
      // Object sits on floor - center at floor + half height
      return floorY + objectHeight / 2;

    case MOUNT_TYPES.WALL:
      // For wall-hung toilets we anchor the TOP of the bbox at the rim
      // height (see MOUNT_HEIGHTS.TOILET_WALL_HUNG_RIM). For other
      // wall-mount fixtures (mirrors, sconces, etc.) keep the existing
      // semantic: mountHeight is the CENTER Y.
      if (objectType === 'toilet' && doc?.mountType === 'wall-hung') {
        return (
          floorY +
          (config.mountHeight || MOUNT_HEIGHTS.TOILET_WALL_HUNG_RIM) -
          objectHeight / 2
        );
      }
      // A mount height that names the prop's TOP (a towel bar's hardware
      // line, with the towel below it) seats the centre half a height lower.
      if (config.mountMeasure === 'top') {
        return floorY + (config.mountHeight || 1.5) - objectHeight / 2;
      }
      return floorY + (config.mountHeight || 1.5);

    case MOUNT_TYPES.CEILING:
      // Object hangs from ceiling
      if (ceilingY !== null) {
        return ceilingY - objectHeight / 2;
      }
      // Fallback if ceiling height not provided
      return floorY + (config.mountHeight || 2.44);

    case MOUNT_TYPES.COUNTER:
      // Deliberately no number. `seatCooktopsOnCounter` and
      // `proceduralizeSinks` have already seated these on the real slab, and
      // nothing here can improve on that — so say "leave Y alone" rather than
      // invent a counter height. Callers MUST honour null; treating it as 0
      // would drop the fixture through the floor.
      return null;

    default:
      return floorY + objectHeight / 2;
  }
}
